import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Button, Group, SimpleGrid, Text } from '@repletoryreach/sdk-ui';
import { SegmentedControl, Switch } from '@repletoryreach/sdk-ui/inputs';
import {
  Alert,
  EmptyState,
  FilterDrawer,
  PageHeader,
  TableShell,
  type FilterField,
  type FilterValues,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { usePriceLadder, useSkuRates } from '../../../data/useSkuRates';
import { useMetaNewsSummary } from '../../../data/useMetaNews';
import { SkuRatesTable, THIN_MARGIN_PERCENT } from './components/SkuRatesTable';
import { SkuRatesList } from './components/SkuRatesList';
import { NewSkuRateForm } from './components/NewSkuRateForm';
import { FxRatesCard } from './components/FxRatesCard';
import { TaxCard } from './components/TaxCard';
import { ImportMetaRatesModal } from './components/ImportMetaRatesModal';
import { StatCard } from '../../../components/StatCard';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { BillingToolbar } from '../components/BillingToolbar';
import { ViewToggle } from '../components/ViewToggle';
import { IconAdjustment, IconGlobe, IconLedger, IconTag } from '../components/icons';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatDate, formatInteger } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';
import './sku-rates.css';

const STAT_COLS = { base: 1, sm: 2, lg: 4 };
const SETTINGS_COLS = { base: 1, lg: 2 };

/** The order the price book reads in: the channel first, then what rides on it. */
const CATEGORIES = ['whatsapp', 'sms', 'email', 'ussd', 'ai', 'knowledge', 'notifications', 'media', 'web', 'meta', 'voice'] as const;

export default function SkuRatesPage() {
  const t = useT();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useSkuRates();
  // The plans on sale, to name what each one pays for a unit. A rate is no
  // longer one figure: the price depends on the customer's plan.
  const ladder = usePriceLadder();
  // Unread pricing news from Meta: the reason to import a rate card, shown where it is imported.
  const metaNews = useMetaNewsSummary().data?.data;
  const all = useMemo(() => data?.data ?? [], [data]);

  const [view, setView] = usePersistedState<ViewMode>('admin.sku-rates.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.sku-rates.pageSize', 25);
  const [category, setCategory] = usePersistedState<string>('admin.sku-rates.category', 'all');
  // Retired versions are bookkeeping. The book opens on what is live.
  const [showRetired, setShowRetired] = usePersistedState<boolean>('admin.sku-rates.retired', false);

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const stats = useMemo(() => {
    // In force today. A rate dated ahead is counted apart: nobody pays it yet.
    const active = all.filter((s) => s.status === 'current');
    const upcoming = all.filter((s) => s.status === 'upcoming');
    const nextStart = upcoming.reduce<string | null>(
      (soonest, s) => (!soonest || s.effective_from < soonest ? s.effective_from : soonest),
      null
    );
    // A SKU can hold one live rate per currency, so the headline figure counts
    // distinct codes rather than rows.
    const activeSkus = new Set(active.map((s) => s.sku_code)).size;
    const currencies = Array.from(new Set(all.map((s) => s.currency))).sort();
    const lastChange = all.filter((s) => s.status !== 'upcoming').reduce<string | null>(
      (newest, s) => (!newest || s.effective_from > newest ? s.effective_from : newest),
      null
    );
    // Margin across the live price book, weighted by nothing (a price list has
    // no volume). The service works each row's margin out on the list price in
    // force, which is never under the floor, so its figure is averaged as it
    // comes and not recomputed from the stored charge. A row priced at nothing
    // has no margin to average.
    const priced = active.filter((s) => s.margin_percent != null);
    const avgMargin = priced.length > 0
      ? Math.round(priced.reduce((sum, s) => sum + (s.margin_percent ?? 0), 0) / priced.length)
      : null;
    const thin = priced.filter((s) => (s.margin_percent ?? 0) < THIN_MARGIN_PERCENT).length;
    return { activeSkus, historical: all.length - active.length - upcoming.length, upcoming: upcoming.length, nextStart, currencies, lastChange, avgMargin, thin };
  }, [all]);

  const filterFields: FilterField[] = useMemo(() => [
    {
      key: 'currency',
      label: t('common.currency'),
      placeholder: t('billing.ledger.filters.anyCurrency'),
      options: stats.currencies.map((c) => ({ value: c, label: c }))
    }
  ], [stats.currencies, t]);

  const hasActiveFilters = Boolean(query.trim()) || Boolean(filters.currency) || category !== 'all';

  function clearAllFilters() {
    setQuery(''); setFilters({}); setCategory('all'); setPage(1);
  }

  const pills: ActiveFilterPill[] = [];
  if (filters.currency) pills.push({ id: 'currency', field: t('common.currency'), value: filters.currency });
  function removePill(id: string) {
    setPage(1);
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = showRetired ? all : all.filter((s) => s.status === 'current' || s.status === 'upcoming');
    if (category !== 'all') rows = rows.filter((s) => s.category === category);
    if (filters.currency) rows = rows.filter((s) => s.currency === filters.currency);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((s) => s.sku_code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q));
    // The book reads by category, then by code; a retired version sits under
    // its live successor, newest first.
    const order = new Map<string, number>(CATEGORIES.map((c, i) => [c, i]));
    return [...rows].sort((a, b) =>
      (order.get(a.category) ?? 99) - (order.get(b.category) ?? 99)
      || a.sku_code.localeCompare(b.sku_code)
      || a.currency.localeCompare(b.currency)
      || Number(b.is_active) - Number(a.is_active)
      || b.effective_from.localeCompare(a.effective_from));
  }, [all, category, filters.currency, query, showRetired]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const categoryOptions = [
    { value: 'all', label: t('billing.skuRates.filters.allCategories') },
    ...CATEGORIES.map((c) => ({ value: c, label: t(`billing.skuRates.category.${c}`) }))
  ];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.skuRates.eyebrow')}
        title={t('billing.skuRates.title')}
        description={t('billing.skuRates.description')}
        actions={
          <Group gap="sm">
            <Button size="sm" variant="outline" onClick={() => setImporting(true)}>{t('billing.skuRates.import.action')}</Button>
            <Button size="sm" onClick={() => setCreating(true)}>{t('billing.skuRates.new.action')}</Button>
            <ViewToggle value={view === 'cards' ? 'table' : view} onChange={setView} />
          </Group>
        }
      />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard
          withRule tone="success" icon={<IconTag />}
          label={t('billing.skuRates.stat.activeSkus')}
          value={formatInteger(stats.activeSkus)}
          hint={t('billing.skuRates.stat.activeSkusHint')}
          loading={isLoading}
        />
        <StatCard
          withRule tone={stats.thin > 0 ? 'warning' : 'info'} icon={<IconGlobe />}
          label={t('billing.skuRates.stat.margin')}
          value={stats.avgMargin == null ? '–' : `${stats.avgMargin}%`}
          hint={stats.thin > 0 ? t('billing.skuRates.stat.marginThin', { count: formatInteger(stats.thin) }) : t('billing.skuRates.stat.marginHint')}
          loading={isLoading}
        />
        <StatCard
          withRule tone="primary" icon={<IconAdjustment />}
          label={t('billing.skuRates.stat.lastChange')}
          value={stats.lastChange ? formatDate(stats.lastChange) : t('billing.skuRates.stat.never')}
          hint={t('billing.skuRates.stat.lastChangeHint')}
          loading={isLoading}
        />
        <StatCard
          withRule tone="neutral" icon={<IconLedger />}
          label={t('billing.skuRates.stat.historical')}
          value={formatInteger(stats.historical)}
          hint={t('billing.skuRates.stat.historicalHint')}
          loading={isLoading}
        />
      </SimpleGrid>

      {stats.upcoming > 0 && stats.nextStart && (
        <Alert color="info">
          {t('billing.skuRates.upcoming.banner', { count: formatInteger(stats.upcoming), date: formatDate(stats.nextStart) })}
        </Alert>
      )}

      {metaNews && metaNews.unread_pricing_count > 0 && (
        <Alert color="warning">
          <Group justify="space-between" gap="sm" wrap="wrap">
            <Text role="bodySmall">
              {metaNews.latest_pricing_item
                ? t('billing.skuRates.metaNews.bannerLatest', { count: formatInteger(metaNews.unread_pricing_count), title: metaNews.latest_pricing_item.title })
                : t('billing.skuRates.metaNews.banner', { count: formatInteger(metaNews.unread_pricing_count) })}
            </Text>
            <Button size="xs" variant="outline" onClick={() => navigate('/billing/meta-updates')}>{t('billing.skuRates.metaNews.open')}</Button>
          </Group>
        </Alert>
      )}

      {importing && <ImportMetaRatesModal opened={importing} onClose={() => setImporting(false)} />}

      {/* The two settings the whole book depends on, side by side: the tax on
          top of it and the way it reaches other currencies. */}
      <SimpleGrid cols={SETTINGS_COLS} spacing="md">
        <TaxCard />
        <FxRatesCard />
      </SimpleGrid>

      <NewSkuRateForm opened={creating} onClose={() => setCreating(false)} currencies={stats.currencies} />

      <BillingToolbar
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('billing.skuRates.search')}
      >
        <SegmentedControl
          size="xs"
          value={category}
          onChange={(v) => { setCategory(v); setPage(1); }}
          data={categoryOptions}
        />
        <Switch
          size="xs"
          checked={showRetired}
          onChange={(v) => { setShowRetired(v); setPage(1); }}
          label={t('billing.skuRates.showRetired')}
        />
        {stats.currencies.length > 1 && (
          <FilterDrawer
            fields={filterFields}
            values={filters}
            onApply={(next) => { setFilters(next); setPage(1); }}
            onClear={clearAllFilters}
          />
        )}
      </BillingToolbar>

      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        totalLabel={t('billing.skuRates.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(showRetired ? all.length : all.filter((s) => s.status === 'current' || s.status === 'upcoming').length) })}
        // The plan names are part of every row, so the table waits for them
        // rather than flash a plan code first. A failed read falls back to codes.
        loading={isLoading || ladder.isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={t('billing.skuRates.empty')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('billing.skuRates.emptyHint')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={
          error ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">{t('billing.skuRates.loadError')}</Text>
                <Group>
                  <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        {view === 'table' && <SkuRatesTable rows={paged} loading={isLoading} showRetired={showRetired} plans={ladder.plans} />}
        {view === 'list' && <SkuRatesList records={paged} showRetired={showRetired} plans={ladder.plans} />}
      </TableShell>
    </Stack>
  );
}
